import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../components/Icon";

const PHASE_STEPS = { url_input: 1, crawling: 1, discovering: 1, auth: 2, testing: 3, generating_errors: 3, complete: 3 };

export default function IntegrationBuilderHeader({ phase, visibility, onClose, integration, onBaseUrlChange }) {
    const { t } = useTranslation();
    const currentStep = PHASE_STEPS[phase] || 1;
    const currentLabel = t(`builder.phases.${phase}`);
    const [editing, setEditing] = useState(false);
    const [editValue, setEditValue] = useState("");
    const inputRef = useRef(null);

    useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

    const startEdit = () => {
        if (!integration?.baseUrl) return;
        setEditValue(integration.baseUrl);
        setEditing(true);
    };

    const saveEdit = () => {
        setEditing(false);
        const trimmed = editValue.trim();
        if (trimmed && trimmed !== integration?.baseUrl && onBaseUrlChange) onBaseUrlChange(trimmed);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") saveEdit();
        if (e.key === "Escape") setEditing(false);
    };

    return (
        <div className="border-b bg-white px-3 py-2 shrink-0">
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                    <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-md shrink-0">
                        <Icon name="ArrowLeft" className="w-4 h-4 text-gray-600" />
                    </button>
                    <span className="font-semibold text-sm text-gray-900 truncate">{integration?.name || t("builder.createIntegration")}</span>
                    {integration?.baseUrl && !editing && (
                        <button onClick={startEdit} className="flex items-center gap-1 hover:bg-gray-100 rounded px-1.5 py-0.5 group" title={t("builder.editBaseUrl")}>
                            <span className="text-[10px] font-mono text-gray-400 truncate max-w-[200px]">{integration.baseUrl}</span>
                            <Icon name="Edit3" className="w-2.5 h-2.5 text-gray-300 group-hover:text-gray-500 shrink-0" />
                        </button>
                    )}
                    {editing && (
                        <div className="flex items-center gap-1">
                            <input
                                ref={inputRef}
                                type="text"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onKeyDown={handleKeyDown}
                                onBlur={saveEdit}
                                className="text-[11px] font-mono px-2 py-0.5 border rounded-md w-56 focus:outline-none focus:ring-1 focus:ring-purple-400"
                            />
                        </div>
                    )}
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium shrink-0 bg-amber-100 text-amber-700">{t("builder.earlyBeta")}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium shrink-0 ${visibility === "public" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                        {visibility === "public" ? t("builder.free") : t("builder.creditsCost", { cost: 300 })}
                    </span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                    {[1, 2, 3].map((step) => (
                        <div key={step} className="flex items-center gap-1">
                            <div className={`w-2 h-2 rounded-full ${step < currentStep ? "bg-green-500" : step === currentStep ? "bg-blue-500" : "bg-gray-200"}`} />
                            {step < 3 && <div className={`w-4 h-0.5 ${step < currentStep ? "bg-green-300" : "bg-gray-200"}`} />}
                        </div>
                    ))}
                    <span className="text-[10px] text-gray-400 ml-1 hidden sm:inline">{currentLabel}</span>
                </div>
            </div>
        </div>
    );
}
